import { Helmet } from "react-helmet";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { useState } from "react";
import {
    FaEye,
    FaPenToSquare,
    FaTrashCan,
    FaServer,
    FaCircleUser,
    FaCircleNodes,
    FaHashtag,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { useHookNas } from "../../../../hooks/useHookNas";
import { useNavigate } from "react-router-dom";
import { NasCreate } from "./modals/NasCreate";
import { NasUpdatePublicKeyWireguard } from "./modals/UpdatePublicKeyWigerguard";
import { NasDeleteConfirmation } from "./modals/Delete";
import { ConnectionStatusBadge } from "../../../../components/badge/ConnectionStatusBadge";

/* ============================================
   Skeleton Loading
   ============================================ */
const SkeletonRow = ({ cols = 8 }) => (
    <tr className="animate-pulse">
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i} className="px-5 py-4">
                <div
                    className="h-3.5 bg-slate-700/50 rounded-md"
                    style={{ width: `${35 + Math.random() * 55}%` }}
                />
            </td>
        ))}
    </tr>
);

const SkeletonCard = () => (
    <div
        className="rounded-2xl p-4 animate-pulse"
        style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
    >
        <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-slate-700/50 flex-shrink-0" />
            <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-700/50 rounded-md w-32" />
                <div className="h-3 bg-slate-700/30 rounded-md w-24" />
                <div className="h-3 bg-slate-700/20 rounded-md w-40" />
            </div>
        </div>
        <div className="flex gap-3 mt-3 pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
            {[...Array(3)].map((_, i) => (
                <div key={i} className="flex-1 h-10 bg-slate-700/25 rounded-lg" />
            ))}
        </div>
    </div>
);

/* ============================================
   Inline Action Buttons (Desktop Table)
   ============================================ */
const ActionBtn = ({ icon: Icon, label, onClick, disabled, color, hoverBg }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed ${hoverBg}`}
        style={{
            background: `${color}15`,
            border: `1px solid ${color}25`,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${color}25`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = `${color}15`; }}
        aria-label={label}
    >
        <Icon className="text-sm" style={{ color }} />
    </button>
);

/* ============================================
   Mobile Action Buttons
   ============================================ */
const MobileActionBtn = ({ icon: Icon, label, onClick, color, hoverBg }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 cursor-pointer ${hoverBg}`}
        style={{
            background: `${color}15`,
            border: `1px solid ${color}25`,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${color}25`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = `${color}15`; }}
        aria-label={label}
    >
        <Icon className="text-sm" style={{ color }} />
    </button>
);

/* ============================================
   Column Config
   ============================================ */
const columns = [
    { key: "action", label: "Aksi", align: "center", width: "130px" },
    { key: "no", label: "No", align: "center", width: "55px" },
    { key: "status", label: "Status", align: "center", width: "120px" },
    { key: "pop", label: "POP", align: "center",  },
    { key: "name", label: "Nama NAS", align: "left" },
    { key: "ip", label: "IP Address", align: "left" },
    { key: "olt", label: "OLT", align: "center", width: "80px" },
    { key: "odp", label: "ODP", align: "center", width: "80px" },
    { key: "user", label: "User PPPoE", align: "center", },
];

/* ============================================
   KOMPONEN: Nas
   ============================================ */
export const Nas = () => {
    const navigate = useNavigate();
    const [showSidebar, setShowSidebar] = useState(true);
    const [openModalCreate, setOpenModalCreate] = useState(false);
    const [openModalUpdatePublicKey, setOpenModalUpdatePublicKey] = useState(false);
    const [openModalDelete, setOpenModalDelete] = useState(false);
    const [selectedNas, setSelectedNas] = useState(null);

    const [keyword, setKeyword] = useState("");
    const { data, loading, refreshData, setParams } = useHookNas();

    /* ---------- Render ---------- */
    return (
        <>
            <Helmet>
                <title>Management NAS - Radius Trend</title>
                <meta
                    name="description"
                    content="Kelola data NAS (Network Access Server) — IP address, monitoring jumlah POP, ODP, dan user PPPoE yang terhubung."
                />
            </Helmet>

            <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
                {/* ===== Header ===== */}
                <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
                    <h2 className="text-base md:text-xl font-display font-bold text-gray-100">
                        Management NAS
                    </h2>
                    <BtnDefault onClick={() => setOpenModalCreate(true)}>
                        <span className="hidden sm:inline">Tambah NAS</span>
                        <span className="sm:hidden">Tambah</span>
                    </BtnDefault>
                </Header>

                {/* ===== Content ===== */}
                <main className="p-4 lg:p-6 space-y-5">
                    {/* ===== Search ===== */}
                    <section
                        className="rounded-2xl p-4"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                            backdropFilter: "blur(12px)",
                        }}
                        aria-label="Pencarian NAS"
                    >
                        <div className="w-full max-w-sm">
                            <div className="relative">
                                <i
                                    className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm"
                                    aria-hidden="true"
                                />
                                <input
                                    type="text"
                                    placeholder="Cari nama atau IP..."
                                    value={keyword}
                                    onChange={(e) => {
                                        setKeyword(e.target.value)
                                        setParams({ keyword: e.target.value })
                                    }}
                                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                />
                            </div>
                        </div>
                    </section>

                    {/* ===== Desktop Table ===== */}
                    <section
                        className="hidden lg:block rounded-2xl overflow-hidden"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                            backdropFilter: "blur(12px)",
                        }}
                        aria-label="Tabel NAS"
                    >
                        <div className="overflow-x-auto">
                            <table className={`${loading ? "min-w-full" : "min-w-max"} max-w-screen-2xl text-sm`}>
                                {/* Head */}
                                <thead>
                                    <tr
                                        className="border-b"
                                        style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}
                                    >
                                        {columns.map((col) => (
                                            <th
                                                key={col.key}
                                                className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"
                                                    }`}
                                                style={col.width ? { width: col.width } : undefined}
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                {/* Body */}
                                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                                    {loading && !data && (
                                        <>{[...Array(6)].map((_, i) => <SkeletonRow key={i} cols={columns.length} />)}</>
                                    )}

                                    {!loading && (!data || data.length === 0) && (
                                        <tr>
                                            <td colSpan={columns.length} className="px-5 py-16 text-center">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div
                                                        className="w-16 h-16 rounded-2xl flex items-center justify-center"
                                                        style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                                                    >
                                                        <FaServer className="text-2xl text-slate-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-slate-400 font-medium">Tidak ada NAS ditemukan</p>
                                                        <p className="text-xs text-slate-500 mt-1">
                                                            {keyword ? "Coba ubah kata kunci pencarian" : "Belum ada NAS yang terdaftar"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {!loading &&
                                        data?.map((item) => (
                                            <tr
                                                key={item.id}
                                                className="transition-colors duration-150"
                                                style={{ background: "transparent" }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                                 {/* Aksi */}
                                                 <td className="px-5 py-3.5">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <ActionBtn
                                                            icon={FaEye}
                                                            label="Lihat detail"
                                                            color="#48CAE4"
                                                            hoverBg="hover:bg-ocean-500/15"
                                                            onClick={() => navigate(`/dashboard/master-data/nas/detail/${item.id}`)}
                                                        />

                                                        {/* <ActionBtn
                                                            icon={FaPenToSquare}
                                                            label="Edit"
                                                            color="#a78bfa"
                                                            hoverBg="hover:bg-violet-500/15"
                                                            onClick={() => {
                                                                setSelectedNas(item);
                                                                setOpenModalUpdatePublicKey(true);
                                                            }}
                                                        /> */}

                                                        <ActionBtn
                                                            icon={FaTrashCan}
                                                            label="Hapus"
                                                            color="#f87171"
                                                            hoverBg="hover:bg-red-500/15"
                                                            onClick={() => {
                                                                setSelectedNas(item);
                                                                setOpenModalDelete(true);
                                                            }}
                                                        />
                                                    </div>
                                                </td>

                                                {/* No */}
                                                <td className="px-5 py-3.5 text-center text-slate-500 font-mono text-xs">
                                                    {data.indexOf(item) + 1}
                                                </td>


                                                {/* Status */}
                                                <td className="px-5 py-3.5 text-center">
                                                    <ConnectionStatusBadge status={item.status} />
                                                </td>

                                                 {/* POP */}
                                                 <td className="px-5 py-3.5 text-center">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-cyan-400 bg-cyan-500/8 border border-cyan-500/10">
                                                        <FaCircleNodes className="text-[10px]" />
                                                        {item.pop?.name ?? 0}
                                                    </span>
                                                </td>

                                                {/* Nama + Server Icon */}
                                                <td className="px-5 py-3.5">
                                                    <div className="flex items-center gap-3">
                                                        <div
                                                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                                            style={{
                                                                background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                                                                border: "1px solid rgba(0,180,216,0.15)",
                                                            }}
                                                        >
                                                            <FaServer className="text-xs text-ocean-500" />
                                                        </div>
                                                        <span className="font-semibold text-gray-100">{item.name}</span>
                                                    </div>
                                                </td>

                                                {/* IP Address */}
                                                <td className="px-5 py-3.5">
                                                    <span className="font-mono text-xs text-ocean-400/90 bg-ocean-500/8 px-2.5 py-1 rounded-lg border border-ocean-500/10">
                                                        {item.ip_address}
                                                    </span>
                                                </td>

                                                {/* OLT */}
                                                <td className="px-5 py-3.5 text-center">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-violet-400 bg-violet-500/8 border border-violet-500/10">
                                                        <FaHashtag className="text-[10px]" />
                                                        {item.olt ?? 0}
                                                    </span>
                                                </td>

                                                {/* ODP */}
                                                <td className="px-5 py-3.5 text-center">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-violet-400 bg-violet-500/8 border border-violet-500/10">
                                                        <FaHashtag className="text-[10px]" />
                                                        {item.odp ?? 0}
                                                    </span>
                                                </td>

                                                {/* User PPPoE */}
                                                <td className="px-5 py-3.5 text-center">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-emerald-400 bg-emerald-500/8 border border-emerald-500/10">
                                                        <FaCircleUser className="text-[10px]" />
                                                        {item.user ?? 0}
                                                    </span>
                                                </td>

                                               
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* ===== Mobile Cards ===== */}
                    <section className="lg:hidden space-y-3" aria-label="Daftar NAS">
                        {loading && !data && <>{[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}</>}

                        {!loading && (!data || data.length === 0) && (
                            <div
                                className="rounded-2xl p-12 text-center"
                                style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                            >
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                                    style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                                >
                                    <FaServer className="text-2xl text-slate-600" />
                                </div>
                                <p className="text-slate-400 font-medium">Tidak ada NAS ditemukan</p>
                                <p className="text-xs text-slate-500 mt-1">{keyword ? "Coba ubah pencarian" : "Belum ada data NAS"}</p>
                            </div>
                        )}

                        {!loading &&
                            data?.map((item) => (
                                <article
                                    key={item.id}
                                    className="rounded-2xl overflow-hidden transition-all duration-200"
                                    style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = "rgba(0, 180, 216, 0.15)";
                                        e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
                                        e.currentTarget.style.background = "rgba(15, 23, 42, 0.5)";
                                    }}
                                >
                                    {/* Header */}
                                    <div className="p-4 flex items-start gap-3" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}>
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{
                                                background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                                                border: "1px solid rgba(0,180,216,0.15)",
                                            }}
                                        >
                                            <FaServer className="text-sm text-ocean-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <h3 className="font-semibold text-gray-100 text-sm truncate">{item.name}</h3>
                                                <ConnectionStatusBadge status={item.status} />
                                            </div>
                                            <p className="font-mono text-xs text-slate-500 mt-0.5">{item.ip_address}</p>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <div className="p-4 grid grid-cols-3 gap-3" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}>
                                        {[
                                            { icon: FaCircleNodes, label: "OLT", value: item?.olt ?? 0, color: "#48CAE4", bg: "rgba(72, 202, 228, 0.08)", border: "rgba(72, 202, 228, 0.12)" },
                                            { icon: FaHashtag, label: "ODP", value: item.odp ?? 0, color: "#a78bfa", bg: "rgba(167, 139, 250, 0.08)", border: "rgba(167, 139, 250, 0.12)" },
                                            { icon: FaCircleUser, label: "User", value: item.user ?? 0, color: "#34d399", bg: "rgba(52, 211, 153, 0.08)", border: "rgba(52, 211, 153, 0.12)" },
                                        ].map((stat, i) => {
                                            const Icon = stat.icon;
                                            return (
                                                <div
                                                    key={i}
                                                    className="text-center p-2.5 rounded-xl"
                                                    style={{ background: stat.bg, border: `1px solid ${stat.border}` }}
                                                >
                                                    <Icon className="text-sm mx-auto mb-1" style={{ color: stat.color }} />
                                                    <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider leading-none mb-1">{stat.label}</p>
                                                    <p className="text-lg font-display font-bold" style={{ color: stat.color }}>{stat.value}</p>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Actions */}
                                    <div className="px-4 py-3 flex justify-end gap-2">
                                        <MobileActionBtn icon={FaEye} label="Lihat" color="#48CAE4" hoverBg="hover:bg-ocean-500/15" onClick={() => navigate(`/dashboard/master-data/nas/detail/${item.id}`)} />
                                        <MobileActionBtn icon={FaPenToSquare} label="Edit" color="#a78bfa" hoverBg="hover:bg-violet-500/15" />
                                        <MobileActionBtn icon={FaTrashCan} label="Hapus" color="#f87171" hoverBg="hover:bg-red-500/15" />
                                    </div>
                                </article>
                            ))}
                    </section>
                </main>
            </DashboardLayout>

            {/* ===== Modal Create ===== */}
            <NasCreate open={openModalCreate} setOpen={setOpenModalCreate} refreshData={refreshData} />
            <NasUpdatePublicKeyWireguard open={openModalUpdatePublicKey} setOpen={setOpenModalUpdatePublicKey} refreshData={refreshData} selectedNas={selectedNas} />
            <NasDeleteConfirmation isOpen={openModalDelete} setIsOpen={setOpenModalDelete} data={selectedNas} refreshData={refreshData} />
        </>
    );
};